import { useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../config/api.config';

export interface FileChangeEvent {
  driveId: string;
  libraryName?: string;
  changeType: 'added' | 'updated' | 'deleted' | 'restored';
  resource: string;
  timestamp: string;
  fileDetails?: {
    id: string;
    name: string;
    webUrl: string;
    mimeType?: string;
    size?: number;
    lastModifiedDateTime: string;
  };
}

export interface SSEOptions {
  driveId?: string; // Filter updates for specific library
  onFileChange?: (event: FileChangeEvent) => void;
  onConnected?: () => void;
  onError?: (error: Event) => void;
}

/**
 * Custom hook for SharePoint Server-Sent Events (SSE)
 * Provides real-time updates when files change in SharePoint
 */
export const useSharePointSSE = (options: SSEOptions = {}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastEvent, setLastEvent] = useState<FileChangeEvent | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    // Don't connect if no callback provided
    if (!options.onFileChange && !options.driveId) {
      return;
    }

    // Build SSE URL
    let sseUrl = `${API_BASE_URL}/sharepoint-webhook/stream`;
    if (options.driveId) {
      sseUrl += `?driveId=${options.driveId}`;
    }

    console.log('[useSharePointSSE] Connecting to:', sseUrl);

    // Create EventSource connection
    const eventSource = new EventSource(sseUrl);
    eventSourceRef.current = eventSource;

    // Handle connection
    eventSource.addEventListener('connected', (event: MessageEvent) => {
      console.log('[useSharePointSSE] ✅ Connected to SSE stream');
      try {
        const data = JSON.parse(event.data);
        console.log('[useSharePointSSE] Connection data:', data);
        setIsConnected(true);
        setError(null);
        options.onConnected?.();
      } catch (parseError) {
        console.error('[useSharePointSSE] Failed to parse connection data:', parseError);
        setError('Invalid connection data');
      }
    });

    // Handle file changes
    eventSource.addEventListener('fileChange', (event: MessageEvent) => {
      console.log('[useSharePointSSE] 📄 File change received');
      try {
        const changeEvent: FileChangeEvent = JSON.parse(event.data);
        console.log('[useSharePointSSE] Change:', changeEvent);
        setLastEvent(changeEvent);
        options.onFileChange?.(changeEvent);
      } catch (parseError) {
        console.error('[useSharePointSSE] Failed to parse file change:', parseError);
        setError('Invalid file change data');
      }
    });

    // Handle heartbeat (keep connection alive)
    eventSource.addEventListener('heartbeat', (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[useSharePointSSE] 💓 Heartbeat:', data.timestamp);
      } catch (parseError) {
        console.error('[useSharePointSSE] Failed to parse heartbeat:', parseError);
      }
    });

    // Handle errors
    eventSource.onerror = (error) => {
      console.error('[useSharePointSSE] ❌ SSE Error:', error);
      setIsConnected(false);
      setError('Connection error. Retrying...');
      options.onError?.(error);
    };

    // Cleanup on unmount
    return () => {
      console.log('[useSharePointSSE] Disconnecting from SSE stream');
      eventSource.close();
      setIsConnected(false);
    };
  }, [options.driveId, options.onFileChange]);

  return {
    isConnected,
    error,
    lastEvent,
    disconnect: () => {
      eventSourceRef.current?.close();
      setIsConnected(false);
    },
  };
};
